import React, { useState, useEffect } from 'react';
import { FaSearch, FaCheck, FaTimes, FaEye, FaDownload, FaFilter, FaFileImage, FaFilePdf, FaCheckCircle, FaTimesCircle, FaClock } from 'react-icons/fa';
import { useApi } from '../hooks/useApi';
import { usePagination } from '../hooks/usePagination';
import Pagination from '../components/Pagination';
import '../styles/DocumentValidation.css';

const DocumentValidation = () => {
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [validationComment, setValidationComment] = useState('');
  const { apiCall } = useApi();

  // Pagination
  const pagination = usePagination(filteredDocuments, 'documents');

  // Reset pagination when filters change
  useEffect(() => {
    pagination.resetPagination();
  }, [searchTerm, statusFilter, typeFilter]);

  // Types de documents avec leurs labels français
  const documentTypes = {
    profile_photo: 'Photo de profil',
    national_id_front: 'CNI Recto',
    national_id_back: 'CNI Verso',
    rccm_document: 'RCCM',
    ifu_document: 'IFU',
    manager_photo: 'Photo Dirigeant'
  };

  // Chargement des documents
  useEffect(() => {
    fetchDocuments();
  }, []);

  // Filtrage des documents
  useEffect(() => {
    let filtered = documents;

    // Filtre par statut
    if (statusFilter !== 'all') {
      filtered = filtered.filter(doc => doc.verification_status === statusFilter);
    }

    // Filtre par type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(doc => doc.document_type === typeFilter);
    }

    // Filtre par recherche (nom du prestataire)
    if (searchTerm) {
      filtered = filtered.filter(doc => 
        doc.prestataire_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.prestataire_email?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredDocuments(filtered);
  }, [documents, statusFilter, typeFilter, searchTerm]);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      // API pour récupérer tous les documents des prestataires
      const response = await apiCall('/admin/documents');
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des documents:', error);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleValidateDocument = async (documentId, status, comment = '') => {
    try {
      const response = await apiCall(`/admin/documents/${documentId}/validate`, {
        method: 'PUT',
        body: JSON.stringify({
          status: status,
          comment: comment
        })
      });

      if (response.success) {
        // Mettre à jour le document localement
        setDocuments(prev => prev.map(doc => 
          doc.id === documentId 
            ? { 
                ...doc, 
                verification_status: status,
                verified_at: new Date().toISOString(),
                verified_by: 'Current Admin',
                rejection_reason: status === 'rejected' ? comment : null
              }
            : doc
        ));
        
        setShowPreviewModal(false);
        setSelectedDocument(null);
        setValidationComment('');
      }
    } catch (error) {
      console.error('Erreur lors de la validation:', error);
      // Pour le dev, simuler la mise à jour
      setDocuments(prev => prev.map(doc => 
        doc.id === documentId 
          ? { 
              ...doc, 
              verification_status: status,
              verified_at: new Date().toISOString(),
              verified_by: 'Current Admin',
              rejection_reason: status === 'rejected' ? comment : null
            }
          : doc
      ));
      setShowPreviewModal(false);
      setSelectedDocument(null);
      setValidationComment('');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'verified':
        return <FaCheckCircle className="status-icon verified" />;
      case 'rejected':
        return <FaTimesCircle className="status-icon rejected" />;
      case 'pending':
      default:
        return <FaClock className="status-icon pending" />;
    }
  };

  const getFileIcon = (documentType, url) => {
    if (url?.endsWith('.pdf')) {
      return <FaFilePdf className="file-icon pdf" />;
    }
    return <FaFileImage className="file-icon image" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return Math.round(bytes / 1024) + ' KB';
    else return Math.round(bytes / 1048576) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Statistiques
  const stats = {
    total: documents.length,
    pending: documents.filter(d => d.verification_status === 'pending').length,
    verified: documents.filter(d => d.verification_status === 'verified').length,
    rejected: documents.filter(d => d.verification_status === 'rejected').length
  };

  return (
    <div className="document-validation-container">
      <div className="page-header">
        <h1>Validation des Documents</h1>
        <p>Gérez et validez les documents des prestataires</p>
      </div>

      {/* Statistiques */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Total Documents</div>
        </div>
        <div className="stat-card pending">
          <div className="stat-value">{stats.pending}</div>
          <div className="stat-label">En Attente</div>
        </div>
        <div className="stat-card verified">
          <div className="stat-value">{stats.verified}</div>
          <div className="stat-label">Vérifiés</div>
        </div>
        <div className="stat-card rejected">
          <div className="stat-value">{stats.rejected}</div>
          <div className="stat-label">Rejetés</div>
        </div>
      </div>

      {/* Filtres et recherche */}
      <div className="filters-section">
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Rechercher par nom ou email du prestataire..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filters">
          <div className="filter-group">
            <FaFilter className="filter-icon" />
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">Tous les statuts</option>
              <option value="pending">En attente</option>
              <option value="verified">Vérifiés</option>
              <option value="rejected">Rejetés</option>
            </select>
          </div>

          <div className="filter-group">
            <select 
              value={typeFilter} 
              onChange={(e) => setTypeFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">Tous les types</option>
              {Object.entries(documentTypes).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Liste des documents */}
      <div className="documents-table-container">
        {loading ? (
          <div className="loading">Chargement des documents...</div>
        ) : pagination.paginatedData.length === 0 ? (
          <div className="no-documents">Aucun document à afficher</div>
        ) : (
          <>
            <table className="documents-table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Prestataire</th>
                  <th>Document</th>
                  <th>Taille</th>
                  <th>Date Upload</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagination.paginatedData.map((document) => (
                  <tr key={document.id}>
                    <td>
                      <div className="document-type">
                        {getFileIcon(document.document_type, document.document_url)}
                      </div>
                    </td>
                    <td>
                      <div className="prestataire-info">
                        <div className="prestataire-name">{document.prestataire_name}</div>
                        <div className="prestataire-email">{document.prestataire_email}</div>
                        <div className="prestataire-type">
                          {document.prestataire_type === 'company' ? 'Entreprise' : 'Individuel'}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="document-name">
                        {documentTypes[document.document_type] || document.document_type}
                      </div>
                    </td>
                    <td className="file-size">{formatFileSize(document.file_size)}</td>
                    <td className="upload-date">{formatDate(document.uploaded_at)}</td>
                    <td>
                      <div className="status-cell">
                        {getStatusIcon(document.verification_status)}
                        <span className={`status-label ${document.verification_status}`}>
                          {document.verification_status === 'pending' && 'En attente'}
                          {document.verification_status === 'verified' && 'Vérifié'}
                          {document.verification_status === 'rejected' && 'Rejeté'}
                        </span>
                      </div>
                      {document.rejection_reason && (
                        <div className="rejection-reason">{document.rejection_reason}</div>
                      )}
                    </td>
                    <td>
                      <div className="actions">
                        <button
                          className="action-btn view"
                          onClick={() => {
                            setSelectedDocument(document);
                            setShowPreviewModal(true);
                          }}
                          title="Voir le document"
                        >
                          <FaEye />
                        </button>
                        {document.verification_status === 'pending' && (
                          <>
                            <button
                              className="action-btn approve"
                              onClick={() => handleValidateDocument(document.id, 'verified')}
                              title="Approuver"
                            >
                              <FaCheck />
                            </button>
                            <button
                              className="action-btn reject"
                              onClick={() => {
                                setSelectedDocument(document);
                                setShowPreviewModal(true);
                              }}
                              title="Rejeter"
                            >
                              <FaTimes />
                            </button>
                          </>
                        )}
                        <a
                          href={document.document_url}
                          download
                          className="action-btn download"
                          title="Télécharger"
                        >
                          <FaDownload />
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {/* Pagination */}
            <Pagination
              totalItems={pagination.totalItems}
              totalPages={pagination.totalPages}
              currentPage={pagination.currentPage}
              pageSize={pagination.pageSize}
              startIndex={pagination.startIndex}
              endIndex={pagination.endIndex}
              hasNextPage={pagination.hasNextPage}
              hasPreviousPage={pagination.hasPreviousPage}
              isFirstPage={pagination.isFirstPage}
              isLastPage={pagination.isLastPage}
              onPageChange={pagination.goToPage}
              onPageSizeChange={pagination.changePageSize}
              config={pagination.config}
            />
          </>
        )}
      </div>

      {/* Modal de prévisualisation et validation */}
      {showPreviewModal && selectedDocument && (
        <div className="modal-overlay" onClick={() => setShowPreviewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Validation du Document</h2>
              <button 
                className="close-modal" 
                onClick={() => {
                  setShowPreviewModal(false);
                  setValidationComment('');
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="modal-body">
              <div className="document-details">
                <div className="detail-row">
                  <span className="label">Prestataire:</span>
                  <span className="value">{selectedDocument.prestataire_name}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Type de document:</span>
                  <span className="value">{documentTypes[selectedDocument.document_type]}</span>
                </div>
                <div className="detail-row">
                  <span className="label">Date d'upload:</span>
                  <span className="value">{formatDate(selectedDocument.uploaded_at)}</span>
                </div>
              </div>

              <div className="document-preview">
                {selectedDocument.document_url?.endsWith('.pdf') ? (
                  <div className="pdf-preview">
                    <FaFilePdf />
                    <p>Aperçu PDF non disponible</p>
                    <a href={selectedDocument.document_url} target="_blank" rel="noopener noreferrer">
                      Ouvrir dans un nouvel onglet
                    </a>
                  </div>
                ) : (
                  <img 
                    src={selectedDocument.document_url} 
                    alt={documentTypes[selectedDocument.document_type]}
                    onError={(e) => {
                      e.target.src = '/placeholder-document.png';
                    }}
                  />
                )}
              </div>

              {selectedDocument.verification_status === 'pending' && (
                <div className="validation-section">
                  <h3>Validation</h3>
                  <textarea
                    placeholder="Commentaire (obligatoire pour un rejet)..."
                    value={validationComment}
                    onChange={(e) => setValidationComment(e.target.value)}
                    rows={3}
                  />
                  <div className="validation-actions">
                    <button
                      className="btn-validate approve"
                      onClick={() => handleValidateDocument(selectedDocument.id, 'verified')}
                    >
                      <FaCheck /> Approuver
                    </button>
                    <button
                      className="btn-validate reject"
                      onClick={() => {
                        if (!validationComment.trim()) {
                          alert('Veuillez fournir une raison pour le rejet');
                          return;
                        }
                        handleValidateDocument(selectedDocument.id, 'rejected', validationComment);
                      }}
                    >
                      <FaTimes /> Rejeter
                    </button>
                  </div>
                </div>
              )}

              {selectedDocument.verification_status !== 'pending' && (
                <div className="validation-info">
                  <h3>Informations de validation</h3>
                  <div className="detail-row">
                    <span className="label">Statut:</span>
                    <span className={`value status ${selectedDocument.verification_status}`}>
                      {selectedDocument.verification_status === 'verified' ? 'Vérifié' : 'Rejeté'}
                    </span>
                  </div>
                  {selectedDocument.verified_at && (
                    <div className="detail-row">
                      <span className="label">Date de validation:</span>
                      <span className="value">{formatDate(selectedDocument.verified_at)}</span>
                    </div>
                  )}
                  {selectedDocument.verified_by && (
                    <div className="detail-row">
                      <span className="label">Validé par:</span>
                      <span className="value">{selectedDocument.verified_by}</span>
                    </div>
                  )}
                  {selectedDocument.rejection_reason && (
                    <div className="detail-row">
                      <span className="label">Raison du rejet:</span>
                      <span className="value">{selectedDocument.rejection_reason}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentValidation;